<template>
  <div class="app-page oauth-callback-page">
    <div class="oauth-callback-content">
      <img class="oauth-callback-logo" src="../assets/img/edgefirst_hexagon.svg" alt="EdgeFirst">
      
      <div class="oauth-callback-status">
        <div class="spinner" />
        <p class="status-message">Authentication in process…</p>
      </div>
    </div>

    <!-- Error dialog -->
    <div v-if="error" class="error-modal-overlay" @click="handleErrorDismiss">
      <div class="error-modal" @click.stop>
        <div class="error-modal-header">
          <h2>Authentication Error</h2>
        </div>
        <div class="error-modal-body">
          <p>{{ error }}</p>
        </div>
        <div class="error-modal-footer">
          <button class="button" @click="handleErrorDismiss">Go Back to Login</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  readOAuthHandshake,
  readOAuthRedirectPath,
  clearOAuthRedirectPath,
  clearOAuthHandshake,
  finishSocialLogin,
} from '@/assets/js/auth/oauth-service';
import { parseJwt } from '@/assets/js/utils.js';
import { safeSessionStorage } from '@/utils/safeStorage';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useProjectsDatasetsStore } from '@/stores/useProjectsDatasetsStore';
import { storeToRefs } from 'pinia';

export default {
  name: 'OAuthCallback',
  setup() {
    const projectsDatasetsStore = useProjectsDatasetsStore();
    const { loadInitialData } = projectsDatasetsStore;
    const { isLoadingInitialData } = storeToRefs(projectsDatasetsStore);
    return { isLoadingInitialData, loadInitialData };
  },
  data() {
    return {
      error: '',
    };
  },
  async created() {
    console.log('[OAuthCallback] Component created');
    await this.handleCallback();
  },
  methods: {
    parseJwt,
    goToLogin() {
      this.$router.replace({ path: '/login' });
    },
    handleErrorDismiss() {
      this.$router.replace({ path: '/login' });
    },

    /**
     * Validates the callback parameters, completes the OAuth exchange with the
     * backend, and persists auth state in exactly the same way as handleSignIn()
     * does in Login.vue.
     */
    async handleCallback() {
      // ── 1. Read callback URL params ──────────────────────────────────────
      const { code, state } = this.$route.query;
      console.log('[OAuthCallback] Starting callback handler', { code: code ? 'present' : 'missing', state: state ? 'present' : 'missing' });

      if (!code || !state) {
        clearOAuthHandshake();
        this.error = 'Authentication failed: missing code or state parameter. Please try again.';
        console.error('[OAuthCallback] Missing code or state');
        return;
      }

      // ── 2. Determine provider ────────────────────────────────────────────
      // First, try to read from sessionStorage (normal flow: user clicked button).
      // If empty, extract from the state JWT (user was redirected here via link/refresh).
      let provider = safeSessionStorage.getItem('oauth_provider');
      console.log('[OAuthCallback] Provider from sessionStorage:', provider);

      if (!provider) {
        // Extract provider from state JWT payload
        try {
          const payload = this.parseJwt(state);
          if (payload && payload.provider) {
            provider = payload.provider;
            console.log('[OAuthCallback] Provider extracted from state JWT:', provider);
          }
        } catch (err) {
          // state is not a valid JWT; continue without extracting provider
          console.warn('[OAuthCallback] Could not parse state as JWT');
        }
      }

      if (!provider) {
        clearOAuthHandshake();
        this.error = 'Authentication failed: provider not found. Please start the login process again.';
        console.error('[OAuthCallback] No provider found');
        return;
      }

      // ── 3. Exchange code for auth token ──────────────────────────────────
      try {
        console.log('[OAuthCallback] Calling finishSocialLogin', { provider, code: code ? 'present' : 'missing' });
        const authResult = await finishSocialLogin(provider, code, state);
        console.log('[OAuthCallback] finishSocialLogin succeeded');

        // Always clear temporary OAuth keys after a terminal outcome
        clearOAuthHandshake();

        // ── 4. Persist auth (mirrors Login.vue handleSignIn logic) ─────────
        const tokenJson = this.parseJwt(authResult.token);
        this.$store.commit('user/setPermissions', tokenJson.permissions);
        this.$store.commit('user/setToken', authResult.token);
        console.log('[OAuthCallback] Auth token committed to store');

        if (authResult.changepassword) {
          // Edge case: provider account requires a password reset
          this.$router.replace({ path: '/login' });
          return;
        }

        // ── 5. Load user profile and navigate ─────────────────────────────
        await this.completeLogin(authResult.username);
      } catch (err) {
        console.error('[OAuthCallback] Error during OAuth exchange:', err);
        clearOAuthHandshake();
        this.error = this.friendlyError(err);
      }
    },

    /**
     * Fetches the full user profile and navigates to the app home or redirect path.
     * Mirrors performUserCheckAndLogin() in Login.vue.
     * @param {string} username
     */
    async completeLogin(username) {
      const dataConnect = new DatastoreConnect();
      console.log('[OAuthCallback] Fetching user profile for:', username);
      const userResp = await dataConnect.checkForUser({ username });

      const userObj = { ...userResp.result };
      userObj.role = userResp.result.role;
      this.$store.commit('user/setUser', userObj);
      console.log('[OAuthCallback] User profile set:', username);

      // Check for redirect path from localStorage (set by OAuth flow) or sessionStorage (set by login page)
      let redirectPath = localStorage.getItem('oauth_redirect_path');
      console.log('[OAuthCallback] redirectPath from localStorage:', redirectPath);
      
      if (!redirectPath) {
        redirectPath = safeSessionStorage.getItem('login_return_path');
        console.log('[OAuthCallback] redirectPath from sessionStorage:', redirectPath);
      }
      
      const targetPath = redirectPath || (userObj.role === 'super' ? '/user' : '/home');
      console.log('[OAuthCallback] Final targetPath:', targetPath);

      // Clear the paths after use
      localStorage.removeItem('oauth_redirect_path');
      safeSessionStorage.removeItem('login_return_path');
      console.log('[OAuthCallback] Cleared redirect paths');

      if (userObj.role === 'super' && !redirectPath) {
        this.$store.commit('appVersion/setAllowDebug', 'yes');
      }

      this.isLoadingInitialData = true;
      console.log('[OAuthCallback] Navigating to', targetPath);
      await this.$router.replace({ path: targetPath });
      await this.loadInitialData();
    },

    /**
     * Maps RPC error objects to user-friendly messages.
     * @param {any} err
     * @returns {string}
     */
    friendlyError(err) {
      if (!err) return 'An unexpected error occurred. Please try again.';

      const code = err.code;
      const message = (err.message || '').toLowerCase();
      console.log('[OAuthCallback] Error details:', { code, message, fullError: err });

      if (code === 404 || message.includes('not configured') || message.includes('provider')) {
        return 'This login provider is not configured on the server.';
      }
      if (code === 401 || message.includes('unauthorized') || message.includes('invalid')) {
        return 'Authentication failed. Please try again.';
      }
      if (message.includes('invitation') || message.includes('invite')) {
        return 'This server accepts registration by invitation only. Please request an invitation.';
      }
      if (err.message) {
        return err.message;
      }
      return 'An unexpected error occurred. Please try again.';
    },
  },
};
</script>

<style lang="scss" scoped>
.oauth-callback-page {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100dvh;
  width: 100%;
  background-color: #250e81 !important;
  background-image: url("@/assets/img/blu-stripes.png") !important;
}

.oauth-callback-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 48px;
}

.oauth-callback-logo {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.oauth-callback-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
}

.status-message {
  font-size: 16px;
  color: #fff;
  margin: 0;
  text-align: center;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Error dialog
.error-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.error-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  max-width: 420px;
  width: 90%;
  overflow: hidden;
}

.error-modal-header {
  padding: 24px 24px 12px;
  border-bottom: 1px solid #e0e0e0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
}

.error-modal-body {
  padding: 24px;
  
  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: #d32f2f;
    font-weight: 500;
  }
}

.error-modal-footer {
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .button {
    min-width: 140px;
    height: 44px;
    border-radius: 6px;
    font-weight: 500;
  }
}

@media (max-width: 640px) {
  .oauth-callback-content {
    gap: 32px;
  }

  .oauth-callback-logo {
    width: 80px;
    height: 80px;
  }

  .error-modal {
    width: 95%;
  }
}
</style>
